import { forwardRef, type ReactNode, useCallback, useEffect, useImperativeHandle, useRef } from 'react'

import { useAdyenCheckout } from '@/hooks/use-adyen-checkout'
import { AdyenCheckoutStatus } from '@/lib/enums'
import { cn } from '@/lib/utils'

import type { SessionType } from '@/lib/enums'
import type { AdyenEnvironment, ErrorWithState, SessionResult } from '@/lib/types'

export interface AdyenDropinHandle {
  submit: () => Promise<SessionResult>
  status: AdyenCheckoutStatus
  /** True once the mounted Apple Pay component's availability check resolves. */
  applePayAvailable: boolean
  /** Submit via Apple Pay — sibling of `submit`, does not affect the Card path. */
  submitApplePay: () => Promise<SessionResult>
  /** True once the mounted Google Pay component's availability check resolves. */
  googlePayAvailable: boolean
}

export interface AdyenDropinProps {
  internalApiHost: string
  authToken: string
  adyenClientKey: string
  adyenEnvironment: AdyenEnvironment
  sessionType: SessionType
  encodedSessionData: string
  /** Required for PAYMENT, ignored for TOKENIZATION. */
  transactionId?: string
  /**
   * The embedder's own declaration of its host, cross-checked against
   * `ancestorOrigins` before Apple Pay's `domainName` is trusted. Absent →
   * falls back to the browser-reported top-level host directly. See
   * `useApplePay` and specs/app/index.md::Apple Pay.
   */
  parentHost?: string
  /** Build-time toggle: capture cardholder name on the card form. */
  showCardholderName?: boolean
  /**
   * When set, the Adyen card form renders a billing address block in the
   * given mode (`'partial'` — postal code only; `'full'` — the complete
   * address block). Omitted/undefined leaves Adyen's default (no fields).
   */
  billingAddressMode?: 'partial' | 'full'
  /**
   * BCP-47 locale tag forwarded to Adyen's checkout. Empty/undefined → the
   * hook defaults to `'en-US'`. See `useAdyenCheckout` for fallback semantics.
   */
  locale?: string
  /**
   * Invoked when the Adyen form's overall validity flips. The underlying
   * hook tracks the last value and only invokes on real flips. Used by
   * `/hpp-session` to emit `card-form-valid` / `card-form-invalid` so the
   * HPP parent can drive its own CTA enabled state.
   */
  onValidityChange?: (isValid: boolean) => void
  /** Called once the Adyen form is mounted into the DOM. */
  onReady?: () => void
  /**
   * Invoked when Apple Pay's availability settles (async, after `onReady`) —
   * callers mirror it into local state to conditionally render their button.
   */
  onApplePayAvailableChange?: (available: boolean) => void
  /**
   * Invoked when Google Pay's availability settles (async, after `onReady`).
   * Unlike Apple Pay there is no caller-supplied button to conditionally
   * render — Adyen renders Google's own button into its own mount div — so
   * this is informational only (e.g. to adjust surrounding layout/spacing).
   */
  onGooglePayAvailableChange?: (available: boolean) => void
  /** Fired once a Google Pay attempt's session result settles as a success. */
  onGooglePayCompleted?: (result: SessionResult) => void
  /** Fired when the shopper dismisses the Google Pay sheet. */
  onGooglePayCancelled?: () => void
  /** Fired on a non-cancellation Google Pay component error. */
  onGooglePayError?: (error: ErrorWithState) => void
  /**
   * Fired synchronously when a Google Pay attempt begins (the component's own
   * `onClick`), before the claim and outcome await. Google Pay's button lives
   * inside the Adyen component, so this is the only hook a route has into
   * "a submit attempt started" — used by `/hpp-session` to emit
   * `card-payment-started` under its own `submitting` dedupe guard.
   */
  onGooglePaySubmitAttempt?: () => void
  className?: string
  /**
   * Rendered ABOVE the Adyen card form (express-checkout position). Typically
   * the Apple Pay button. Hidden with the wrapper on session completion.
   */
  applePaySlot?: ReactNode
  /**
   * Rendered alongside the Adyen card form inside the same wrapper. Typically
   * the submit Button. Hidden together with the Adyen form once the session
   * reaches a terminal success state.
   */
  children?: ReactNode
}

/**
 * Registry-shaped React wrapper around the Adyen drop-in card form.
 *
 * Design intent: seed for a future shadcn registry of Adyen primitives shared
 * with `jetpay-frontend`. Does NOT call `postMessage` directly — message
 * dispatch is the caller's responsibility via the imperative `submit()` on the
 * forwarded ref.
 *
 * Layout: a wrapper div with two children — an inner `<div>` that Adyen mounts
 * its preact tree into, plus any caller-supplied `children` (typically the
 * submit Button). Adyen's preact `render(component, container)` wipes the
 * container's children, so the Adyen mount target MUST be its own dedicated
 * div, not shared with React children.
 *
 * Visibility: once the session settles successfully (status COMPLETED), the
 * whole wrapper renders nothing. Mirrors webpack-era `showButton` going false
 * on success — except now the form's empty container is removed from the DOM
 * too. Refusal / form-invalid / error paths keep status at READY / SUBMITTING /
 * FAILED so the user can retry.
 *
 * Bit-exact behaviour preserved:
 *   - `showPayButton: false` (caller renders its own submit via children).
 *   - WEB_DROP_IN_SHOW_NAME → cardholder-name capture conditional.
 *   - Adyen callbacks unmount the form component on terminal states; the
 *     wrapper then hides itself one render later via `status === COMPLETED`.
 */
export const AdyenDropin = forwardRef<AdyenDropinHandle, AdyenDropinProps>(function AdyenDropin(props, ref) {
  const adyenMountRef = useRef<HTMLDivElement>(null)
  const applePayMountRef = useRef<HTMLDivElement>(null)
  const googlePayMountRef = useRef<HTMLDivElement>(null)
  const {
    status,
    mount,
    submit,
    applePayAvailable,
    mountApplePay,
    submitApplePay,
    googlePayAvailable,
    mountGooglePay,
  } = useAdyenCheckout({
    internalApiHost: props.internalApiHost,
    authToken: props.authToken,
    adyenClientKey: props.adyenClientKey,
    adyenEnvironment: props.adyenEnvironment,
    sessionType: props.sessionType,
    encodedSessionData: props.encodedSessionData,
    transactionId: props.transactionId,
    parentHost: props.parentHost,
    showCardholderName: props.showCardholderName,
    billingAddressMode: props.billingAddressMode,
    locale: props.locale,
    onValidityChange: props.onValidityChange,
    onGooglePayCompleted: props.onGooglePayCompleted,
    onGooglePayCancelled: props.onGooglePayCancelled,
    onGooglePayError: props.onGooglePayError,
    onGooglePaySubmitAttempt: props.onGooglePaySubmitAttempt,
  })

  const handleSubmit = useCallback(() => submit(), [submit])

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit,
      status,
      applePayAvailable,
      submitApplePay,
      googlePayAvailable,
    }),
    [handleSubmit, status, applePayAvailable, submitApplePay, googlePayAvailable]
  )

  const { onReady } = props
  useEffect(() => {
    if (!adyenMountRef.current) return
    let cancelled = false
    mount(adyenMountRef.current).then(() => {
      if (cancelled) return
      onReady?.()
      // After mount() — each wallet reuses the checkout session it created.
      if (applePayMountRef.current) void mountApplePay(applePayMountRef.current)
      if (googlePayMountRef.current) void mountGooglePay(googlePayMountRef.current)
    })
    return () => {
      cancelled = true
    }
  }, [mount, onReady, mountApplePay, mountGooglePay])

  const { onApplePayAvailableChange } = props
  useEffect(() => {
    onApplePayAvailableChange?.(applePayAvailable)
  }, [applePayAvailable, onApplePayAvailableChange])

  const { onGooglePayAvailableChange } = props
  useEffect(() => {
    onGooglePayAvailableChange?.(googlePayAvailable)
  }, [googlePayAvailable, onGooglePayAvailableChange])

  if (status === AdyenCheckoutStatus.COMPLETED) return null

  return (
    <div className={cn('adyen-dropin flex flex-col gap-4', props.className)}>
      {props.applePaySlot}
      {/* Adyen renders Google's own button here — visible, unlike Apple Pay's mount div. */}
      <div
        ref={googlePayMountRef}
        className={cn(status === AdyenCheckoutStatus.SUBMITTING && 'pointer-events-none opacity-50')}
      />
      {(props.applePaySlot || googlePayAvailable) && <hr className="border-border" />}
      <div ref={adyenMountRef} />
      <div ref={applePayMountRef} />
      {props.children}
    </div>
  )
})
